import React, { useEffect, useRef, useState } from "react";
import { Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, matchPath, useHistory } from "react-router-dom";
import { Slide } from "@progress/kendo-react-animation";
import { Loader } from "@progress/kendo-react-indicators";
import { emptyUnclearedNotifications, setShowNotifications, setUnclearedNotifications } from "../../actions/notifications";
import { setIsApprovalFilterSelected } from "../../actions/projects";
import { setReminderId } from "../../actions/reminders";
import { getFocusedSection, setExpanded } from "../../actions/taskSidebar";
import { route } from "../../config/";
import { icon, label, number, quote, tooltip } from "../../config/constants";
import { commentDateAndTimeFormat, getTaskUrl, getTimeFromDate, sanitizeHtmlString } from "../../helper/common";
import { dateConvert, sanitizeUrl, splitString, useCloseOnClickOutsideGlobal } from "../../utils/common";
import { isJoinRequestNotification } from "../../utils/joinRequest.helper";
import { handleLibraryAttachmentClick, useFolderHelpers } from "../ContentManagementSystem/helper";
import { handleCloseSidebar } from "../Tasks/tasks.common";
import { openSidebar } from "../Tasks/tasks.service";
import { getNotificationMessage } from "./notification.helper";
import "./notification.scss";
import { getNotifications, getNotificationsCount, markNotificationAsRead, readAllNotifications } from "./notifications.service";

const Notifications = () => {
  const [notificationLoading, setNotificationLoading] = useState(false);
  const dispatch = useDispatch();
  const { id, myProjectId } = useSelector((state) => state.auth.user);
  const { unclearedNotifications, commentCount, reminderCount, approvalCount } = useSelector((state) => state.notifications);
  const { fetchAndSetFolder } = useFolderHelpers();
  const userId = id;

  const notificationDropRef = useRef(null),
    history = useHistory();

  useEffect(() => {
    handleGetNotification();
  }, []);

  useCloseOnClickOutsideGlobal(notificationDropRef, "Notifications", setShowNotifications);

  /**
   * handles get notifications
   * @param {void}
   * @returns {void}
   */
  const handleGetNotification = async () => {
    dispatch(emptyUnclearedNotifications());
    setNotificationLoading(true);
    await dispatch(getNotifications(userId));
    await dispatch(getNotificationsCount(id));
    setNotificationLoading(false);
  };

  /**
   *  Returns redirection URL
   * @returns {String} URL
   */
  const getUrl = (EntityType, Id, ProjectId, ProjectTaskID, CurrentProject) => {
    if (EntityType === "Tasks" || EntityType === "Reminder") {
      const task = { Id: Id, ProjectTaskId: ProjectTaskID, ProjectId: ProjectId, CurrentProject: CurrentProject };
      return sanitizeUrl(getTaskUrl(task.taskId));
    }
  };

  /**
   *  Clears all notifications
   * @returns {void}
   */
  const handleClearAllButton = () => {
    dispatch(readAllNotifications(userId));
    dispatch(emptyUnclearedNotifications());
  };

  const handleShowMore = () => {
    dispatch(setShowNotifications(false));
    handleCloseSidebar(dispatch, { isNewTask: false });
  };

  /**
   * handles approval icon click action
   * @author Muskan Thakur
   */
  const handleApprovalIconClick = () => {
    dispatch(setIsApprovalFilterSelected(true));
    dispatch(setShowNotifications(false));
  };

  /**
   *  Sets reminderId for notifiaction Popup
   * @returns {void}
   */

  const handlepopup = (reminderId, notifId) => {
    dispatch(setReminderId(reminderId, notifId));
  };

  /**
   *  handles notification click
   * @params {Object}
   */
  const handleNotificationClick = async ({
    Id,
    reminderId,
    notifId,
    EntityType,
    ProjectId,
    ProjectName,
    QueueId,
    QueueName,
    mongoEntityId,
    ReminderType,
    notificationType,
    NotificationMessage,
  }) => {
    if (EntityType == label.ADMIN) {
      history.push(route.ADMIN_ROUTE.MANAGE_USERS.PATH);
    } else if (EntityType == label.PROJECT) {
      /**
       * Check if this is a join request notification and redirect to members tab
       */
      if (isJoinRequestNotification({ notificationType, NotificationMessage, EntityType }) && ProjectId) {
        history.push(`${route.PRIVATE_ROUTE.TEAM_SETTING.PATH}/${ProjectId}/members`);
      } else {
        history.push(`${route.PRIVATE_ROUTE.TASKS.PATH}/${ProjectId}/${ProjectName}`);
      }
    } else if (EntityType == label.QUEUE_ENTITY) {
      history.push(`${route.PRIVATE_ROUTE.QUEUE_TASKS.PATH}/${QueueId}/${QueueName}`);
    } else if (Id) {
      await openSidebar({ id: Id, userId: id });
      if (ReminderType == number.FIVE || notificationType === number.TWO) {
        dispatch(setExpanded(["comments"]));
        dispatch(getFocusedSection("comments"));
      }
      handlepopup(reminderId, notifId);
    } else if (mongoEntityId) {
      handleLibraryAttachmentClick(mongoEntityId, history, fetchAndSetFolder);
    }
    dispatch(setShowNotifications(false));
    dispatch(markNotificationAsRead({ notifId, userId }));
  };

  /**
   * @author Sejal
   * It is used to clear notification in notification popup
   */
  const clearNotification = ({ notifId }) => {
    const notifications = unclearedNotifications.filter((noti) => noti.notifId != notifId);
    dispatch(setUnclearedNotifications(notifications));
    dispatch(markNotificationAsRead({ notifId, id }));
  };

  /**
   * handles notification with animation
   * @param { void }
   * @returns {JSX}
   */
  const notificationsWithAnimation = () => {
    return (
      <Slide direction='right' className='k-list-scroller'>
        {unclearedNotifications?.length !== number.ZERO ? (
          [...unclearedNotifications].map((notification, idx) => {
            const { Id, notifId, reminderId, CreatedOn, EntityType, Icon, Name, userName, PhotoLinks } = notification;
            return (
              <div key={notifId} className='single-notification'>
                <div className='notification-details d-flex'>
                  {notification?.notificationType === number.TEN ? (
                    <div
                      onClick={() => {
                        handleNotificationClick(notification);
                      }}
                      className={"notification-reminder-icon mr-2"}>
                      <span className='notification-reminder-icon'>{icon.TASK_ESCALATION}</span>
                    </div>
                  ) : (
                    <div
                      onClick={() => {
                        handleNotificationClick(notification);
                      }}
                      className={`${EntityType === tooltip.REMINDER ? "notification-reminder-icon" : "notification-profile-image"} mr-2`}>
                      {PhotoLinks?.small ? (
                        <img className='profile-picture rounded-circle object-fit-cover mr-2' src={PhotoLinks?.small} />
                      ) : (
                        <span
                          className={`${
                            EntityType === tooltip.REMINDER
                              ? "notification-reminder-icon"
                              : "profile-picture rounded-circle object-fit-cover cursor-pointer"
                          }`}>
                          {EntityType === tooltip.REMINDER ? icon.BELL_ICON : icon.ACCOUNT_CIRCLE}
                        </span>
                      )}
                    </div>
                  )}
                  <div className='w-100'>
                    <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
                      <div className='d-flex justify-content-between'>
                        <div>
                          {notification?.notificationType === number.TEN ? (
                            <span
                              className='notification-user-name mr-3 cursor-pointer'
                              onClick={() => {
                                handleNotificationClick(notification);
                              }}>
                              {"Task Escalation"}
                            </span>
                          ) : (
                            <span
                              className='notification-user-name mr-3 cursor-pointer'
                              onClick={() => {
                                handleNotificationClick(notification);
                              }}>
                              {userName ? userName : splitString(getNotificationMessage(notification), " ")[number.ZERO]}
                            </span>
                          )}
                          <span className='notification-type-icon cursor-default' title={tooltip.NOTIFICATION_TYPE}>
                            {icon[Icon]}
                          </span>
                        </div>
                        <div>
                          <span className='notification-time'>{commentDateAndTimeFormat(CreatedOn)}</span>
                        </div>
                      </div>
                      <div className='notification-tick'>
                        <p
                          className='notification-text mb-0'
                          onClick={() => {
                            handleNotificationClick(notification);
                          }}
                          dangerouslySetInnerHTML={{ __html: sanitizeHtmlString(getNotificationMessage(notification)) }}></p>
                        <span
                          className='notification-check-icon cursor-pointer'
                          onClick={() => clearNotification(notification)}
                          title={tooltip.MARK_AS_READ}>
                          {icon.CHECK_CIRCLE}
                        </span>
                      </div>
                    </Tooltip>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className='no-notification-message'>{quote.NO_NEW_NOTIFICATIONS}</div>
        )}
      </Slide>
    );
  };

  return (
    <div className='notifications-container position-fixed'>
      <div className='notifications' ref={notificationDropRef}>
        <div className='notifications-header position-relative d-flex justify-content-between align-items-center'>
          <div className='d-flex'>
            <span className='header-title mr-3'>{quote.NOTIFICATIONS}</span>
            <div className='notifications-actions d-flex justify-content-center align-items-center'>
              <Link className='text-decoration-none' to={`${route.PRIVATE_ROUTE.COMMENT.PATH}`} onClick={handleShowMore}>
                <span
                  className={`notifications-action-item d-flex justify-content-center align-items-center cursor-pointer ${
                    commentCount ? "new-comments" : ""
                  } `}>
                  {icon.COMMENT} <span className={`notifications-count ml-1`}>{commentCount}</span>
                </span>
              </Link>
              <Link className='text-decoration-none' to={`${route.PRIVATE_ROUTE.TASKS.PATH}`}>
                <span
                  className={`notifications-action-item d-flex justify-content-center align-items-center ml-2 cursor-pointer ${
                    approvalCount ? "new-approvals" : ""
                  } `}
                  onClick={handleApprovalIconClick}>
                  {icon.APPROVAL_ICON}
                  <span className={`notifications-count ml-1 `}>{approvalCount}</span>
                </span>
              </Link>
              <Link className='text-decoration-none' to={`${route.PRIVATE_ROUTE.REMINDER.PATH}`} onClick={handleShowMore}>
                <span
                  id='notification-reminder-icon'
                  className={`notifications-action-item d-flex justify-content-center  ${
                    reminderCount ? "new-reminders" : ""
                  } align-items-center ml-2 cursor-pointer`}>
                  {icon.BELL_ICON} <span className={`notifications-count ml-1 `}>{reminderCount}</span>
                </span>
              </Link>
            </div>
          </div>
          <button className='notification-clear-all-btn' onClick={handleClearAllButton}>
            {label.MARK_AS_READ}
          </button>
        </div>
        <div className='notifications-content overflow-y-auto'>
          {notificationLoading ? (
            <div className='text-center'>
              <Loader size='medium' type={"infinite-spinner"} />
            </div>
          ) : (
            notificationsWithAnimation()
          )}
        </div>
        <div className='notifications-footer d-flex justify-content-center align-items-center'>
          <Link to={`${route.PRIVATE_ROUTE.NOTIFICATION.PATH}`} onClick={handleShowMore} id='show-more-recent-history-click'>
            {label.VIEW_ALL_NOTIFICATIONS}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Notifications;
